import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, FlatList, StyleSheet, TouchableOpacity, Alert} from 'react-native';
import {
  Text, Button, Chip,
  Searchbar, Portal, Modal, RadioButton, Divider,
} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import GradientHeader from '../../components/common/GradientHeader';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useCustomerStore} from '../../store/useCustomerStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {colors, spacing} from '../../theme';

const STEPS = [
  {key: 'customer', icon: 'account-outline', label: 'Customer'},
  {key: 'items', icon: 'tshirt-crew-outline', label: 'Items'},
  {key: 'review', icon: 'clipboard-check-outline', label: 'Review'},
];

const StepIndicator = ({currentStep}) => (
  <View style={styles.stepBar}>
    {STEPS.map((step, idx) => {
      const isActive = idx === currentStep;
      const isDone = idx < currentStep;
      return (
        <View key={step.key} style={styles.stepItem}>
          <View style={[
            styles.stepCircle,
            isActive && styles.stepCircleActive,
            isDone && styles.stepCircleDone,
          ]}>
            {isDone ? (
              <Icon name="check" size={14} color={colors.white} />
            ) : (
              <Text style={[styles.stepNumber, isActive && styles.stepNumberActive]}>
                {idx + 1}
              </Text>
            )}
          </View>
          <Text style={[styles.stepLabel, isActive && styles.stepLabelActive]}>
            {step.label}
          </Text>
          {idx < STEPS.length - 1 && (
            <View style={[styles.stepLine, isDone && styles.stepLineDone]} />
          )}
        </View>
      );
    })}
  </View>
);

const OrderCreateScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {customers, fetchCustomers, searchCustomers} = useCustomerStore();
  const {orgUnits, selectedOrgUnitId, fetchOrgUnits} = useOrgUnitStore();

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [storeId, setStoreId] = useState(selectedOrgUnitId);
  const [storeModalVisible, setStoreModalVisible] = useState(false);

  useEffect(() => {
    fetchCustomers({page: 0, size: 50});
    if (orgUnits.length === 0) {
      fetchOrgUnits();
    }
  }, [fetchCustomers, orgUnits.length, fetchOrgUnits]);

  useEffect(() => {
    if (orgUnits.length === 1 && !storeId) {
      setStoreId(orgUnits[0].id);
    }
  }, [orgUnits, storeId]);

  useEffect(() => {
    if (route.params?.newCustomer) {
      setSelectedCustomer(route.params.newCustomer);
      setCustomerSearch('');
      navigation.setParams({newCustomer: undefined});
    }
  }, [route.params?.newCustomer, navigation]);

  const handleCustomerSearch = useCallback(query => {
    setCustomerSearch(query);
    if (query.trim().length > 0) {
      searchCustomers(query.trim());
    } else {
      fetchCustomers({page: 0, size: 50});
    }
  }, [searchCustomers, fetchCustomers]);

  const handleAddCustomer = useCallback(() => {
    const query = customerSearch.trim();
    const isPhone = /^\d{5,}$/.test(query);
    navigation.navigate('CustomerForm', {
      returnTo: 'OrderCreate',
      prefillPhone: isPhone ? query : '',
      prefillName: isPhone ? '' : query,
    });
  }, [customerSearch, navigation]);

  const handleNext = useCallback(() => {
    if (!storeId) {
      Alert.alert(t('common.error'), 'Please select a store');
      return;
    }
    if (!selectedCustomer) {
      Alert.alert(t('common.error'), 'Please select a customer');
      return;
    }
    navigation.navigate('SelectItem', {
      orgUnitId: storeId,
      customerId: selectedCustomer.id,
      customerName: selectedCustomer.fullName,
      customerGender: selectedCustomer.gender,
      orderId: null,
      existingLines: [],
    });
  }, [storeId, selectedCustomer, navigation, t]);

  const displayedCustomers = customers.slice(0, 10);
  const hasSearchQuery = customerSearch.trim().length > 0;
  const noResults = hasSearchQuery && displayedCustomers.length === 0;

  return (
    <View style={styles.container}>
      <GradientHeader title={t('order.createOrder')} onBack={() => navigation.goBack()} />

      {/* Step indicator */}
      <StepIndicator currentStep={0} />

      <ScrollView style={styles.scrollView} keyboardShouldPersistTaps="handled">

        {/* Store selection */}
        {orgUnits.length > 1 && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Icon name="store-outline" size={18} color={colors.primary} />
              <Text style={styles.sectionTitle}>{t('order.selectStore')}</Text>
            </View>
            {orgUnits.length <= 4 ? (
              <View style={styles.storeRow}>
                {orgUnits.map(store => (
                  <Chip
                    key={store.id}
                    selected={storeId === store.id}
                    showSelectedCheck={storeId === store.id}
                    selectedColor={storeId === store.id ? colors.white : undefined}
                    onPress={() => setStoreId(store.id)}
                    style={[styles.storeChip, storeId === store.id && styles.storeChipActive]}
                    textStyle={storeId === store.id ? styles.storeChipTextActive : styles.storeChipText}>
                    <Text style={{fontSize: 13, color: storeId === store.id ? '#fff' : '#000', marginTop: 0}}>{store.name}</Text>
                  </Chip>
                ))}
              </View>
            ) : (
              <>
                <TouchableOpacity
                  style={styles.storeDropdown}
                  onPress={() => setStoreModalVisible(true)}
                  activeOpacity={0.7}>
                  <Icon name="store-outline" size={20} color={storeId ? colors.primary : colors.textSecondary} />
                  <Text style={[styles.storeDropdownText, storeId && styles.storeDropdownTextSelected]} numberOfLines={1}>
                    {storeId ? orgUnits.find(s => s.id === storeId)?.name : t('order.selectStore')}
                  </Text>
                  <Icon name="chevron-down" size={20} color={colors.textSecondary} />
                </TouchableOpacity>
                <Portal>
                  <Modal
                    visible={storeModalVisible}
                    onDismiss={() => setStoreModalVisible(false)}
                    contentContainerStyle={styles.storeModal}>
                    <Text style={styles.storeModalTitle}>{t('order.selectStore')}</Text>
                    <Divider />
                    <FlatList
                      data={orgUnits}
                      keyExtractor={item => String(item.id)}
                      renderItem={({item}) => (
                        <TouchableOpacity
                          style={styles.storeOption}
                          onPress={() => { setStoreId(item.id); setStoreModalVisible(false); }}>
                          <RadioButton.Android
                            value={String(item.id)}
                            status={storeId === item.id ? 'checked' : 'unchecked'}
                            onPress={() => { setStoreId(item.id); setStoreModalVisible(false); }}
                            color={colors.primary}
                          />
                          <View style={styles.storeOptionContent}>
                            <Text style={styles.storeOptionName}>{item.name}</Text>
                            {item.city && <Text style={styles.storeOptionCity}>{item.city}</Text>}
                          </View>
                        </TouchableOpacity>
                      )}
                    />
                  </Modal>
                </Portal>
              </>
            )}
          </View>
        )}

        {/* Customer selection */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Icon name="account-outline" size={18} color={colors.primary} />
            <Text style={styles.sectionTitle}>{t('order.selectCustomer')}</Text>
          </View>

          {selectedCustomer ? (
            <View style={styles.selectedCustomer}>
              <View style={styles.selectedCustomerLeft}>
                <View style={styles.selectedAvatar}>
                  <Text style={styles.selectedAvatarText}>
                    {selectedCustomer.fullName.charAt(0).toUpperCase()}
                  </Text>
                </View>
                <View>
                  <Text style={styles.selectedName}>{selectedCustomer.fullName}</Text>
                  <Text style={styles.selectedPhone}>{selectedCustomer.phone}</Text>
                </View>
              </View>
              <TouchableOpacity onPress={() => setSelectedCustomer(null)} style={styles.changeBtn}>
                <Text style={styles.changeBtnText}>Change</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <>
              <Searchbar
                placeholder={t('customer.searchPlaceholder')}
                value={customerSearch}
                onChangeText={handleCustomerSearch}
                style={styles.searchbar}
              />
              {noResults ? (
                <View style={styles.noResultsContainer}>
                  <Icon name="account-search-outline" size={48} color={colors.textLight} />
                  <Text style={styles.noResultsText}>
                    No customers found for "{customerSearch.trim()}"
                  </Text>
                  <Button
                    mode="contained"
                    icon="account-plus"
                    onPress={handleAddCustomer}
                    style={styles.addCustomerButton}
                    buttonColor={colors.primary}>
                    Add New Customer
                  </Button>
                </View>
              ) : (
                <>
                  {displayedCustomers.map(c => (
                    <TouchableOpacity
                      key={c.id}
                      onPress={() => setSelectedCustomer(c)}
                      style={styles.customerRow}>
                      <View style={styles.customerAvatar}>
                        <Text style={styles.customerAvatarText}>
                          {(c.fullName || '?').charAt(0).toUpperCase()}
                        </Text>
                      </View>
                      <View style={{flex: 1}}>
                        <Text style={styles.customerName}>{c.fullName}</Text>
                        <Text style={styles.customerPhone}>{c.phone}</Text>
                      </View>
                      <Icon name="chevron-right" size={20} color={colors.textLight} />
                    </TouchableOpacity>
                  ))}
                  <TouchableOpacity style={styles.addCustomerRow} onPress={handleAddCustomer} activeOpacity={0.7}>
                    <View style={styles.addCustomerIcon}>
                      <Icon name="account-plus-outline" size={18} color={colors.primary} />
                    </View>
                    <Text style={styles.addCustomerRowText}>Add New Customer</Text>
                  </TouchableOpacity>
                </>
              )}
            </>
          )}
        </View>
      </ScrollView>

      <View style={styles.bottomBar}>
        <Button mode="contained" onPress={handleNext} disabled={!selectedCustomer || !storeId}
          style={styles.bottomButton} buttonColor={colors.primary}
          contentStyle={styles.bottomButtonContent}>
          {t('common.next')}
        </Button>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  scrollView: {flex: 1},

  // Step indicator
  stepBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.divider,
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepCircle: {
    width: 26,
    height: 26,
    borderRadius: 13,
    borderWidth: 2,
    borderColor: colors.border,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.surface,
  },
  stepCircleActive: {
    borderColor: colors.primary,
    backgroundColor: colors.primary,
  },
  stepCircleDone: {
    borderColor: colors.success,
    backgroundColor: colors.success,
  },
  stepNumber: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.textSecondary,
  },
  stepNumberActive: {
    color: colors.white,
  },
  stepLabel: {
    fontSize: 11,
    fontWeight: '500',
    color: colors.textSecondary,
    marginLeft: 4,
  },
  stepLabelActive: {
    color: colors.primary,
    fontWeight: '700',
  },
  stepLine: {
    width: 24,
    height: 2,
    backgroundColor: colors.border,
    marginHorizontal: 6,
  },
  stepLineDone: {
    backgroundColor: colors.success,
  },

  // Sections
  section: {
    backgroundColor: colors.surface,
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: spacing.md,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.text,
  },

  // Store selection
  storeRow: {
    flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs,
  },
  storeChip: {backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border},
  storeChipActive: {backgroundColor: colors.primary, borderColor: colors.primary},
  storeChipText: {color: colors.text, fontSize: 13},
  storeChipTextActive: {color: colors.white, fontSize: 13},
  storeDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
    gap: spacing.sm,
  },
  storeDropdownText: {
    flex: 1, fontSize: 15, color: colors.textSecondary,
  },
  storeDropdownTextSelected: {
    color: colors.text, fontWeight: '500',
  },
  storeModal: {
    backgroundColor: colors.surface,
    marginHorizontal: spacing.lg,
    borderRadius: 12,
    maxHeight: '60%',
    overflow: 'hidden',
  },
  storeModalTitle: {
    fontSize: 16, fontWeight: '600', color: colors.text, padding: spacing.md,
  },
  storeOption: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: spacing.xs, paddingRight: spacing.md,
  },
  storeOptionContent: {flex: 1},
  storeOptionName: {fontSize: 15, color: colors.text},
  storeOptionCity: {fontSize: 12, color: colors.textSecondary},

  // Selected customer
  selectedCustomer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.success + '08',
    borderWidth: 1.5,
    borderColor: colors.success + '40',
    borderRadius: 10,
    padding: spacing.md,
  },
  selectedCustomerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  selectedAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.success,
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectedAvatarText: {
    fontSize: 18, fontWeight: '700', color: colors.white,
  },
  selectedName: {fontSize: 15, fontWeight: '600', color: colors.text},
  selectedPhone: {fontSize: 13, color: colors.textSecondary, marginTop: 1},
  changeBtn: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    backgroundColor: colors.background,
  },
  changeBtnText: {fontSize: 13, fontWeight: '600', color: colors.primary},

  // Customer search & list
  searchbar: {marginBottom: spacing.sm, backgroundColor: colors.background, elevation: 0},
  customerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.divider,
    gap: spacing.sm,
  },
  customerAvatar: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: colors.primary + '15',
    justifyContent: 'center',
    alignItems: 'center',
  },
  customerAvatarText: {
    fontSize: 14, fontWeight: '700', color: colors.primary,
  },
  customerName: {fontSize: 14, fontWeight: '500', color: colors.text},
  customerPhone: {fontSize: 12, color: colors.textSecondary, marginTop: 1},

  noResultsContainer: {
    alignItems: 'center', paddingVertical: spacing.xl, paddingHorizontal: spacing.md,
  },
  noResultsText: {
    color: colors.textSecondary, fontSize: 14, textAlign: 'center',
    marginTop: spacing.sm, marginBottom: spacing.lg,
  },
  addCustomerButton: {borderRadius: 8},
  addCustomerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.md,
    gap: spacing.sm,
  },
  addCustomerIcon: {
    width: 34,
    height: 34,
    borderRadius: 17,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  addCustomerRowText: {
    color: colors.primary, fontSize: 14, fontWeight: '600',
  },

  // Bottom bar
  bottomBar: {
    padding: spacing.md, backgroundColor: colors.surface,
    borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: colors.divider,
  },
  bottomButton: {borderRadius: 8},
  bottomButtonContent: {paddingVertical: 4},
});

export default OrderCreateScreen;
